
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import { Box, Button, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';

// ----------------------------------------------------------------------

const BRAND = '#032C65';
const BRAND_LIGHT = '#f0f4ff';

function truncateUrl(url, maxLen = 45) {
  if (!url) return '';
  try {
    const { hostname, pathname } = new URL(url);
    const full = `${hostname}${pathname}`;
    return full.length > maxLen ? `${full.substring(0, maxLen)}…` : full;
  } catch {
    return url.length > maxLen ? `${url.substring(0, maxLen)}…` : url;
  }
}

ResourceCard.propTypes = {
  resource: PropTypes.object.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default function ResourceCard({ resource, onDelete }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hashid, name, url, icon_photo: iconPhoto } = resource;

  return (
    <Grid item xs={12} sm={6} md={3}>
      <Box
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          background: '#fff',
          border: '1px solid rgba(3,44,101,0.12)',
          boxShadow: '0 2px 12px rgba(3,44,101,0.08)',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          transition: 'box-shadow 0.2s, transform 0.2s',
          '&:hover': { boxShadow: '0 6px 24px rgba(3,44,101,0.13)', transform: 'translateY(-2px)' },
        }}
      >
        {/* Image */}
        <Box
          sx={{
            height: 200,
            background: iconPhoto ? `url(${iconPhoto}) center/cover no-repeat` : BRAND_LIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {!iconPhoto && <ImageIcon sx={{ fontSize: 64, color: BRAND, opacity: 0.18 }} />}
        </Box>

        {/* Content */}
        <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color={BRAND}
            sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
            title={name}
          >
            {name}
          </Typography>

          {url && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
              title={url}
            >
              {truncateUrl(url)}
            </Typography>
          )}

          {url && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<LinkIcon />}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ alignSelf: 'flex-start', mt: 0.5, textTransform: 'none', borderRadius: 2 }}
            >
              {t('open_url') || 'Open URL'}
            </Button>
          )}
        </Box>

        {/* Footer actions */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 0.5,
            background: '#fafbff',
          }}
        >
          <Tooltip title={t('edit')}>
            <IconButton size="small" onClick={() => navigate(`/dashboard/resources/edit/${hashid}`)} sx={{ color: BRAND }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('delete')}>
            <IconButton size="small" onClick={() => onDelete(hashid)} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Grid>
  );
}

